// eslint-disable-next-line import/no-unresolved
import './style.scss';

import { useMemo, useState, useEffect } from 'react';
import { Link, useLocation, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useQuery } from 'react-query';
import { Column } from 'primereact/column';
import { ColumnGroup } from 'primereact/columngroup';
import { Row } from 'primereact/row';
import { Tag } from 'primereact/tag';
import { DataTable } from 'primereact/datatable';
import { Button } from 'primereact/button';
import { Dialog } from 'primereact/dialog';
import { Dropdown } from 'primereact/dropdown';
import { InputText } from 'primereact/inputtext';
// eslint-disable-next-line no-unused-vars
import { getAllUserStatistics, getYears } from 'apis/statistics.api';
// import { Can } from 'components/Can';
import Pagination from 'components/Pagination';
import { mapError } from 'utils/func';
import SearchFilters from './components/SearchFilters';
import CouncilsTable from './components/CouncilsTable';

export default function UserStatistics() {
  // #region Data
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const [pagination, setPagination] = useState({
    first: parseInt(searchParams.get('first'), 10) || 0,
    rowsPerPage: 10,
  });
  const { t } = useTranslation();

  const [visibleOwnerProposal, setVisibleOwnerProposal] = useState(false);
  const [listOwnProposal, setListOwnProposal] = useState([]);
  const [visibleJoinProposal, setVisibleJoinProposal] = useState(false);
  const [listJoinProposal, setListJoinProposal] = useState([]);
  const [visibleCouncils, setVisibleCouncils] = useState(false);
  const [selectedUserId, setSelectedUserId] = useState(null);

  const [isVisibleSearchFilters, setIsVisibleSearchFilters] = useState(false);
  const [filtersTable, setFiltersTable] = useState({
    _programYears: { value: null },
    _fullname: { value: null },
    _gender: { value: null },
    _email: { value: null },
  });
  // background filters, sync with localStorage
  const [filters, setFilters] = useState({
    programYears: null,
    fullname: null,
    gender: null,
    email: null,
  });

  // applied filters, change every time click on Apply and Clear button for update
  const [appliedFilters, setAppliedFilters] = useState({
    programYears: null,
    fullname: null,
    gender: null,
    email: null,
  });

  const genderOptions = [
    { value: 'male', name: 'Nam' },
    { value: 'female', name: 'Nữ' },
  ];

  const {
    data: _data,
    isLoading,
    isError,
    error,
  } = useQuery(
    ['usersStatistics', pagination, appliedFilters],
    async ({ queryKey: [, _pagination, _filters] }) => {
      const result = await getAllUserStatistics({ ..._pagination, ..._filters });
      return result;
    }
  );

  const data = useMemo(
    () => _data?.data ?? { data: [], pagination: { row: 10, totalRecords: 0 } },
    [_data]
  );

  const { data: yearData } = useQuery(['year'], getYears);
  const programYears = useMemo(() => yearData?.data ?? [], [yearData]);
  useEffect(() => {
    const localFilters = JSON.parse(
      localStorage.getItem(`admin-${location.pathname.slice(1)}-filters`) ?? '{}'
    );

    setFilters({
      ...localFilters,
    });
    setAppliedFilters({
      ...localFilters,
    });
    // Set rows per page in localStorage
    const localRowsPerPage = parseInt(
      localStorage.getItem(`admin-${location.pathname.slice(1)}-rowsPerPage`),
      10
    );
    if (localRowsPerPage) {
      setPagination((cur) => ({ ...cur, rowsPerPage: localRowsPerPage }));
    }
    // Set current page (first) with searchParams
    const currentFirst = parseInt(searchParams.get('first'), 10);
    if (currentFirst) {
      setPagination((cur) => ({ ...cur, first: currentFirst }));
    }
  }, []);

  // #region Render

  const fullnameFilter = () => (
    <InputText
      value={filters.fullname}
      onChange={(e) => {
        const newFilters = { ...filters, fullname: e.target.value };
        setFilters(newFilters);
      }}
      placeholder={t('employee.viewList.searchByFullname')}
      className="p-column-filter"
    />
  );

  const emailFilter = () => (
    <InputText
      value={filters.email}
      onChange={(e) => {
        const newFilters = { ...filters, email: e.target.value };
        setFilters(newFilters);
      }}
      placeholder={t('employee.viewList.searchByEmail')}
      className="p-column-filter"
    />
  );

  const genderFilter = () => (
    <Dropdown
      value={filters.gender}
      options={genderOptions}
      optionLabel="name"
      optionValue="value"
      onChange={(e) => {
        const newFilters = { ...filters, gender: e.value };
        setFilters(newFilters);
      }}
      placeholder={t('statistic.gender')}
      className="p-column-filter"
      showClear
    />
  );

  const filterClearTemplate = (options) => (
    <Button
      type="button"
      label={t('employee.viewList.clear')}
      onClick={() => {
        options.filterClearCallback();
      }}
      severity="secondary"
      outlined
    />
  );

  const filterApplyTemplate = (options) => (
    <Button
      type="button"
      label={t('employee.viewList.apply')}
      onClick={() => {
        options.filterApplyCallback();
        localStorage.setItem(
          `admin-${location.pathname.slice(1)}-filters`,
          JSON.stringify(filters)
        );
      }}
      severity="success"
    />
  );

  const filterActiveStyle = (field) =>
    filters[field] && (filters[field]?.length > 0 || Object.values(filters[field]).some((e) => e))
      ? { color: '#22C55E' }
      : { color: '#6c757d' };

  const renderHeader = (
    <ColumnGroup>
      <Row>
        <Column
          header={t('statistic.fullname')}
          rowSpan={2}
          headerStyle={filterActiveStyle('fullname')}
          className="w-24rem"
          style={{ minWidth: '14rem' }}
          filter
          filterField="_fullname"
          filterElement={fullnameFilter}
          filterClear={filterClearTemplate}
          filterApply={filterApplyTemplate}
          showFilterMenu
          showFilterMatchModes={false}
          onFilterClear={() => {
            const newFilters = { ...filters, fullname: '' };
            setFilters(newFilters);
            setAppliedFilters(newFilters);
            localStorage.setItem(
              `admin-${location.pathname.slice(1)}-filters`,
              JSON.stringify(newFilters)
            );
          }}
          onFilterApplyClick={() => {
            setAppliedFilters(filters);
          }}
        />
        <Column
          header={t('statistic.gender')}
          className="w-10rem text-center"
          rowSpan={2}
          filter
          filterField="_gender"
          filterElement={genderFilter}
          filterClear={filterClearTemplate}
          filterApply={filterApplyTemplate}
          showFilterMenu
          showFilterMatchModes={false}
          onFilterClear={() => {
            const newFilters = { ...filters, gender: null };
            setFilters(newFilters);
            setAppliedFilters(newFilters);
            localStorage.setItem(
              `admin-${location.pathname.slice(1)}-filters`,
              JSON.stringify(newFilters)
            );
          }}
          onFilterApplyClick={() => {
            setAppliedFilters(filters);
          }}
          headerStyle={filterActiveStyle('gender')}
        />
        <Column
          header={t('statistic.email')}
          className="w-20rem "
          rowSpan={2}
          filter
          filterField="_email"
          filterElement={emailFilter}
          filterClear={filterClearTemplate}
          filterApply={filterApplyTemplate}
          showFilterMenu
          showFilterMatchModes={false}
          onFilterClear={() => {
            const newFilters = { ...filters, email: '' };
            setFilters(newFilters);
            setAppliedFilters(newFilters);
            localStorage.setItem(
              `admin-${location.pathname.slice(1)}-filters`,
              JSON.stringify(newFilters)
            );
          }}
          onFilterApplyClick={() => {
            setAppliedFilters(filters);
          }}
          headerStyle={filterActiveStyle('email')}
        />
        <Column header={t('statistic.phone')} className="w-20rem text-center" rowSpan={2} />

        <Column
          header={t('statistic.proposalQuantity')}
          className="w-15rem text-center"
          colSpan={2}
        />

        <Column
          header={t('statistic.councilQuantity')}
          className="w-15rem text-center"
          rowSpan={2}
        />
      </Row>
      <Row>
        <Column header={t('statistic.ownerProjectProposal')} className="w-20rem text-center" />
        <Column header={t('statistic.joinProjectProposal')} className="w-20rem text-center" />
      </Row>
    </ColumnGroup>
  );

  // convert data.userList from array of array to array
  const flattenedUserList = data.userList ? data.userList.flat() : [];

  // owner proposal
  const onClickOwnerProposal = (rowData) => {
    setListOwnProposal(
      rowData?.ownerProjectProposals.map((item) => {
        const leadOrganization = item?.projectProposalOrganizations?.find(
          (org) => org?.type === 'LEAD'
        );
        return {
          id: item?.id,
          vietnameseName: item?.vietnameseName,
          projectType: item?.projectType,
          program: item?.program?.name,
          leadOrganizationVietnamese: leadOrganization?.name,
          leadOrganizationEnglish: leadOrganization?.englishName,
          status: item?.status?.name,
        };
      })
    );
    setVisibleOwnerProposal(true);
  };

  const onHideOwnerProposal = () => {
    setVisibleOwnerProposal(false);
  };

  const closeOwnerProposal = () => (
    <div>
      <Button
        label={t('formLayout.action.close')}
        type="button"
        className="w-7rem"
        severity="danger"
        onClick={onHideOwnerProposal}
      />
    </div>
  );

  const formartOwnerProposal = (rowData) => (
    <div
      style={rowData?.countOwnerProjectProposal > 0 ? { cursor: 'pointer', color: 'blue' } : {}}
      onClick={() => {
        if (rowData?.countOwnerProjectProposal > 0) {
          onClickOwnerProposal(rowData);
        }
      }}
    >
      {rowData?.countOwnerProjectProposal}
    </div>
  );

  // join proposal
  const onClickJoinProposal = (rowData) => {
    setListJoinProposal(
      rowData?.joinedProjectProposals.map((item) => {
        const leadOrganization = item?.projectProposalOrganizations?.find(
          (org) => org?.type === 'LEAD'
        );
        return {
          id: item?.id,
          vietnameseName: item?.vietnameseName,
          projectType: item?.projectType,
          program: item?.program?.name,
          leadOrganizationVietnamese: leadOrganization?.name,
          leadOrganizationEnglish: leadOrganization?.englishName,
          status: item?.status?.name,
        };
      })
    );
    setVisibleJoinProposal(true);
  };

  const onHideJoinProposal = () => {
    setVisibleJoinProposal(false);
  };

  const closeJoinProposal = () => (
    <div>
      <Button
        label={t('formLayout.action.close')}
        type="button"
        className="w-7rem"
        severity="danger"
        onClick={onHideJoinProposal}
      />
    </div>
  );

  const formartJoinProposal = (rowData) => (
    <div
      style={rowData?.countJoinProjectProposal > 0 ? { cursor: 'pointer', color: 'blue' } : {}}
      onClick={() => {
        if (rowData?.countJoinProjectProposal > 0) {
          onClickJoinProposal(rowData);
        }
      }}
    >
      {rowData?.countJoinProjectProposal}
    </div>
  );

  // councils joined
  const onClickCouncilsJoin = (rowData) => {
    if (rowData?.countTotalCouncil > 0) {
      setSelectedUserId(rowData?.id);
      setVisibleCouncils(true);
    }
  };

  const onHideCouncilsJoin = () => {
    setVisibleCouncils(false);
    setSelectedUserId(null);
  };

  const closeCouncils = () => (
    <div>
      <Button
        label={t('formLayout.action.close')}
        type="button"
        className="w-7rem"
        severity="danger"
        onClick={onHideCouncilsJoin}
      />
    </div>
  );

  const formartCountCouncilJoined = (rowData) => (
    <div
      style={rowData?.countTotalCouncil > 0 ? { cursor: 'pointer', color: 'blue' } : {}}
      onClick={() => onClickCouncilsJoin(rowData)}
    >
      {rowData?.countTotalCouncil}
    </div>
  );

  const formatGender = (rowData) => {
    const gender = (
      rowData?.gender ??
      rowData?.scientificProfile?.gender ??
      rowData?.user?.scientificProfile?.gender ??
      ''
    ).toLowerCase();

    const genderMapping = {
      female: 'Nữ',
      male: 'Nam',
    };

    return genderMapping[gender] ?? '';
  };

  // Formart name proposal
  const formatNameProposal = (value) => (
    <Link to={`/projects-revise/${value?.id}`} className="hover:underline">
      {value?.vietnameseName}
    </Link>
  );

  // Formart projectType
  const formatProjectType = (value) => t(`projectTypes.${value.projectType}`);

  // Formart organization
  const formatOrganization = (value) =>
    t('lang') === 'vi' ? value?.leadOrganizationVietnamese : value?.leadOrganizationEnglish;

  // Formart status
  const formatStatus = (value) => {
    const statusColorMapping = {
      revise: 'warning',
      new: 'info',
      submit: 'success',
      approved: 'success',
    };

    return (
      <Tag
        style={{ maxWidth: '8rem' }}
        severity={statusColorMapping[value?.status]}
        value={t(`statuses.${value?.status}`)}
        rounded
      />
    );
  };
  return (
    <div>
      {isError && <h3 className="text-red-600">{mapError(t, error)}</h3>}
      <div className="flex justify-content-between align-items-center pb-4">
        <h4 className="m-0 hidden sm:block text-xl sm:text-2xl">{t('statistic.listUser')}</h4>
      </div>

      <div className="flex justify-content-between mb-4">
        <div>
          <Button
            type="button"
            icon="pi pi-filter"
            label={t('projectProposal.filter')}
            outlined
            onClick={() => {
              setIsVisibleSearchFilters(true);
            }}
          />
        </div>
        {filters?.programYears?.length !== 0 && filters?.programYears !== null && (
          <div>
            <h4>{t('projectProposal.year')}</h4>
            <p className="display-current-filter">{filters?.programYears?.join(', ')}</p>
          </div>
        )}
      </div>

      <DataTable
        value={flattenedUserList}
        className="p-datatable-gridlines header-table"
        headerColumnGroup={renderHeader}
        dataKey="id"
        filters={filtersTable}
        filterDisplay="menu"
        onFilter={(e) => setFiltersTable(e.filters)}
        loading={isLoading}
        emptyMessage={t('statistic.emptyMessage')}
      >
        <Column field="full_name" />
        <Column body={formatGender} className="text-center" />
        <Column header={t('employee.email')} field="email" />
        <Column field="phone" className="text-center" alignFrozen="right" />

        <Column
          field="countOwnerProjectProposal"
          className="w-8rem text-center"
          frozen
          alignFrozen="right"
          body={formartOwnerProposal}
        />
        <Column
          field="countJoinProjectProposal"
          className="w-8rem text-center"
          frozen
          alignFrozen="right"
          body={formartJoinProposal}
        />

        <Column
          field="countTotalCouncil"
          className="text-center"
          alignFrozen="right"
          body={formartCountCouncilJoined}
        />
      </DataTable>
      <Pagination
        pagination={pagination}
        setPagination={setPagination}
        paginationData={data.pagination}
        pageName="userAllStatistics"
      />
      {isVisibleSearchFilters && (
        <SearchFilters
          visible={isVisibleSearchFilters}
          setVisible={setIsVisibleSearchFilters}
          filters={filters}
          setFilters={setFilters}
          appliedFilters={appliedFilters}
          setAppliedFilters={setAppliedFilters}
          programYears={programYears}
        />
      )}

      {/* owner proposal */}
      <Dialog
        header={t('statistic.listOwnerProjectProposal')}
        visible={visibleOwnerProposal}
        style={{ width: '70vw', maxHeight: '80vh' }}
        footer={closeOwnerProposal('displayBasic')}
        onHide={onHideOwnerProposal}
        headerStyle={{ color: '#3B82F6' }}
      >
        <DataTable
          value={listOwnProposal}
          className="p-datatable-gridlines header-table"
          emptyMessage={t('statistic.emptyMessage')}
        >
          <Column
            field="index"
            header={t('statistic.no')}
            body={(rowData) => listOwnProposal.indexOf(rowData) + 1}
            style={{ textAlign: 'center', width: '7%' }}
          />
          <Column
            body={formatNameProposal}
            header={t('statistic.vietnameseName')}
            style={{ textAlign: 'left', minWidth: '30rem' }}
          />
          <Column
            body={formatProjectType}
            header={t('statistic.projectType')}
            style={{ textAlign: 'left' }}
          />
          <Column field="program" header={t('statistic.program')} style={{ textAlign: 'left' }} />
          <Column
            body={formatOrganization}
            header={t('statistic.leadOrganization')}
            style={{ textAlign: 'left' }}
          />
          <Column
            body={formatStatus}
            header={t('statistic.status')}
            style={{ textAlign: 'center' }}
          />
        </DataTable>
      </Dialog>

      {/* join proposal */}
      <Dialog
        header={t('statistic.listJoinProjectProposal')}
        visible={visibleJoinProposal}
        style={{ width: '70vw', maxHeight: '80vh' }}
        footer={closeJoinProposal('displayBasic')}
        onHide={onHideJoinProposal}
        headerStyle={{ color: '#3B82F6' }}
      >
        <DataTable
          value={listJoinProposal}
          className="p-datatable-gridlines header-table"
          emptyMessage={t('statistic.emptyMessage')}
        >
          <Column
            field="index"
            header={t('statistic.no')}
            body={(rowData) => listJoinProposal.indexOf(rowData) + 1}
            style={{ textAlign: 'center', width: '7%' }}
          />
          <Column
            body={formatNameProposal}
            header={t('statistic.vietnameseName')}
            style={{ textAlign: 'left', minWidth: '10rem' }}
          />
          <Column
            body={formatProjectType}
            header={t('statistic.projectType')}
            style={{ textAlign: 'left' }}
          />
          <Column field="program" header={t('statistic.program')} style={{ textAlign: 'left' }} />
          <Column
            body={formatOrganization}
            header={t('statistic.leadOrganization')}
            style={{ textAlign: 'left' }}
          />
          <Column
            body={formatStatus}
            header={t('statistic.status')}
            style={{ textAlign: 'center' }}
          />
        </DataTable>
      </Dialog>

      {/* councils joined */}
      <Dialog
        header={t('statistic.listJoinCouncil')}
        visible={visibleCouncils}
        style={{ width: '80vw', maxHeight: '80vh' }}
        footer={closeCouncils('displayBasic')}
        onHide={onHideCouncilsJoin}
        headerStyle={{ color: '#3B82F6' }}
      >
        {selectedUserId && <CouncilsTable userId={selectedUserId} />}
      </Dialog>
    </div>
  );
}
